import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getMsg } from "../../utils/antdStatic";
import { apiFetch, type OpcionesLlamada } from "../../services/apiClient";
import { propiedadesApi } from "../../services/propiedades.api";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";

/**
 * Backend (Sequelize) -> Frontend (camelCase)
 * Fuente de campos: property.model.js
 */

export type Propiedad = {
  id: number;

  idPropiedadCorto?: string | null;
  idPropiedadLargo?: string | null;

  nombrePropiedad: string;
  nombrePublicacion?: string | null;

  propietarioIds: number[];
  idCliente?: number | null;

  pais?: number | null;
  region?: number | null;
  comuna?: number | null;
  sector?: number | null;

  calle?: string | null;
  numeroCalle?: number | null;

  casaODepto?: string | null;
  torreOConjunto?: string | null;
  piso?: number | null;
  numeroDeptoCasa?: string | null;

  nombreCondominio?: string | null;
  estacionamiento?: string | null;

  otros?: string | null;

  precioLimpiezaCobrado?: number | null;
  precioLimpiezaAseador?: number | null;
  // Legacy (valor total único) — se conservan por compatibilidad.
  precioTransporte?: number | null;
  precioMateriales?: number | null;
  precioLavanderia?: number | null;
  // Desglose neto/IVA/total — obligatorios para el cálculo financiero del
  // arriendo (Ingreso AF y Cliente). Ver docs/modulo-arriendos-precios.md §Desglose de limpieza.
  precioTransporteNeto?: number | null;
  precioTransporteIva?: number | null;
  precioTransporteTotal?: number | null;
  precioMaterialesNeto?: number | null;
  precioMaterialesIva?: number | null;
  precioMaterialesTotal?: number | null;
  precioLavanderiaNeto?: number | null;
  precioLavanderiaIva?: number | null;
  precioLavanderiaTotal?: number | null;

  comisionPropiedad?: number | null;
  plan?: string | null;

  // Precio BASE mínimo por noche acordado con el propietario (lo que paga el
  // arrendatario, NO el monto a recibir por el dueño). Piso del "precio por
  // noche base". Si en el calendario se fija un precio menor, el sistema
  // advierte. Opcional (null = sin mínimo). Ver docs/modulo-arriendos-precios.md §32.
  precioMinimoNoche?: number | null;

  claveWifi?: string | null;
  claveCerradura?: string | null;

  mensajeInicial?: string | null;

  lavanderiaId?: number | null;

  habilitado: boolean;

  /** Avisos por correo al cliente de esta propiedad (round 2026-07-28). */
  correosClienteActivos: boolean;

  // Si tu backend devuelve nombres parseados (opcional)
  // paisNombre?: string | null;
  // regionNombre?: string | null;
  // comunaNombre?: string | null;
  // sectorNombre?: string | null;

  // owner?: { id: number; displayName: string; email?: string | null } | null;
  // client?: { id: number; displayName: string; email?: string | null } | null;
};

export type UsuarioCreate = {
  nombre_propietario?: string | null;
  email_propietario?: string | null;
  telefono_propietario?: string | null;

  nombre_usuario?: string | null;
  email_usuario?: string | null;
  telefono_usuario?: string | null;

  nombre_quien_recibe?: string | null;
  email_quien_recibe?: string | null;
  telefono_quien_recibe?: string | null;

  rol: string; // escalar: "cliente" | "aseador" | ... (post-refactor)
};

export type PropiedadUpsert = Omit<Propiedad, "id">;

type ApiPropiedad = {
  id: number;
  id_propiedad_corto?: string | null;
  id_propiedad_largo?: string | null;

  nombrepropiedad: string;
  nombre_publicacion?: string | null;

  propietario_ids?: number[] | null;
  id_cliente?: number | null;

  pais?: number | null;
  region?: number | null;
  comuna?: number | null;
  sector?: number | null;

  calle?: string | null;
  numero_calle?: number | null;

  casa_o_depto?: string | null;
  torre_o_conjunto?: string | null;
  piso?: number | null;
  numero_depto_casa?: string | null;

  nombre_condominio?: string | null;
  estacionamiento?: string | null;

  otros?: string | null;

  precio_limpieza_cobrado?: number | null;
  precio_limpieza_aseador?: number | null;
  precio_transporte?: number | null;
  precio_materiales?: number | null;
  precio_lavanderia?: number | null;
  precio_transporte_neto?: number | null;
  precio_transporte_iva?: number | null;
  precio_transporte_total?: number | null;
  precio_materiales_neto?: number | null;
  precio_materiales_iva?: number | null;
  precio_materiales_total?: number | null;
  precio_lavanderia_neto?: number | null;
  precio_lavanderia_iva?: number | null;
  precio_lavanderia_total?: number | null;

  comision_propiedad?: number | null;
  plan?: string | null;

  precio_minimo_noche?: number | null;

  clave_wifi?: string | null;
  clave_cerradura?: string | null;

  mensaje_inicial?: string | null;

  lavanderia_id?: number | null;

  habilitado?: boolean;

  correos_cliente_activos?: boolean;

  // Si incluyes parses desde backend, pueden venir campos extra
  // paisNombre?: string | null;
  // regionNombre?: string | null;
  // comunaNombre?: string | null;
  // sectorNombre?: string | null;
  // owner?: any;
  // client?: any;
};

type CountryRow = { id: number; pais: string };
type RegionRow = { id: number; region: string; pais: number };
type ComunaRow = { id: number; comuna: string; region: number };
type SectorRow = { id: number; sector: string; comuna: number };
// `es_generica` identifica la fila "Lavandería externa" del sistema, que es
// el default obligatorio cuando una propiedad no tiene una AF interna asignada.
// La UI lo usa para preseleccionarla en propiedades nuevas y marcarla con tag.
type LavanderiaRow = { id: number; nombre: string; activo: boolean; es_generica?: boolean };

export function apiToUi(p: ApiPropiedad): Propiedad {
  return {
    id: p.id,
    idPropiedadCorto: p.id_propiedad_corto ?? null,
    idPropiedadLargo: p.id_propiedad_largo ?? null,

    nombrePropiedad: p.nombrepropiedad,
    nombrePublicacion: p.nombre_publicacion ?? null,

    propietarioIds: Array.isArray(p.propietario_ids) ? p.propietario_ids : [],
    idCliente: p.id_cliente ?? null,

    pais: p.pais ?? null,
    region: p.region ?? null,
    comuna: p.comuna ?? null,
    sector: p.sector ?? null,

    calle: p.calle ?? null,
    numeroCalle: p.numero_calle ?? null,

    casaODepto: p.casa_o_depto ?? null,
    torreOConjunto: p.torre_o_conjunto ?? null,
    piso: p.piso ?? null,
    numeroDeptoCasa: p.numero_depto_casa ?? null,

    nombreCondominio: p.nombre_condominio ?? null,
    estacionamiento: p.estacionamiento ?? null,

    otros: p.otros ?? null,

    precioLimpiezaCobrado: p.precio_limpieza_cobrado ?? null,
    precioLimpiezaAseador: p.precio_limpieza_aseador ?? null,
    precioTransporte: p.precio_transporte ?? null,
    precioMateriales: p.precio_materiales ?? null,
    precioLavanderia: p.precio_lavanderia ?? null,
    precioTransporteNeto: p.precio_transporte_neto ?? null,
    precioTransporteIva: p.precio_transporte_iva ?? null,
    precioTransporteTotal: p.precio_transporte_total ?? null,
    precioMaterialesNeto: p.precio_materiales_neto ?? null,
    precioMaterialesIva: p.precio_materiales_iva ?? null,
    precioMaterialesTotal: p.precio_materiales_total ?? null,
    precioLavanderiaNeto: p.precio_lavanderia_neto ?? null,
    precioLavanderiaIva: p.precio_lavanderia_iva ?? null,
    precioLavanderiaTotal: p.precio_lavanderia_total ?? null,

    comisionPropiedad: p.comision_propiedad ?? null,
    plan: p.plan ?? null,

    precioMinimoNoche: p.precio_minimo_noche ?? null,

    claveWifi: p.clave_wifi ?? null,
    claveCerradura: p.clave_cerradura ?? null,

    mensajeInicial: p.mensaje_inicial ?? null,

    lavanderiaId: p.lavanderia_id ?? null,

    habilitado: p.habilitado !== false,

    // `!== false` (y no `=== true`): una propiedad guardada antes de que
    // existiera la columna llega sin el campo y tiene que contar como
    // habilitada, igual que el default de la DB.
    correosClienteActivos: p.correos_cliente_activos !== false,
  };
}

function uiToApi(p: Partial<PropiedadUpsert>): Partial<ApiPropiedad> {
  return {
    id_propiedad_corto: p.idPropiedadCorto ?? null,
    id_propiedad_largo: p.idPropiedadLargo ?? null,

    nombrepropiedad: p.nombrePropiedad as any,
    nombre_publicacion: p.nombrePublicacion ?? null,

    propietario_ids: Array.isArray(p.propietarioIds) && p.propietarioIds.length > 0 ? p.propietarioIds : [],
    id_cliente: p.idCliente ?? null,

    pais: p.pais ?? null,
    region: p.region ?? null,
    comuna: p.comuna ?? null,
    sector: p.sector ?? null,

    calle: p.calle ?? null,
    numero_calle: p.numeroCalle ?? null,

    casa_o_depto: p.casaODepto ?? null,
    torre_o_conjunto: p.torreOConjunto ?? null,
    piso: p.piso ?? null,
    numero_depto_casa: p.numeroDeptoCasa ?? null,

    nombre_condominio: p.nombreCondominio ?? null,
    estacionamiento: p.estacionamiento ?? null,

    otros: p.otros ?? null,

    precio_limpieza_cobrado: p.precioLimpiezaCobrado ?? null,
    precio_limpieza_aseador: p.precioLimpiezaAseador ?? null,
    precio_transporte: p.precioTransporte ?? null,
    precio_materiales: p.precioMateriales ?? null,
    precio_lavanderia: p.precioLavanderia ?? null,
    precio_transporte_neto: p.precioTransporteNeto ?? null,
    precio_transporte_iva: p.precioTransporteIva ?? null,
    precio_transporte_total: p.precioTransporteTotal ?? null,
    precio_materiales_neto: p.precioMaterialesNeto ?? null,
    precio_materiales_iva: p.precioMaterialesIva ?? null,
    precio_materiales_total: p.precioMaterialesTotal ?? null,
    precio_lavanderia_neto: p.precioLavanderiaNeto ?? null,
    precio_lavanderia_iva: p.precioLavanderiaIva ?? null,
    precio_lavanderia_total: p.precioLavanderiaTotal ?? null,

    comision_propiedad: p.comisionPropiedad ?? null,
    plan: p.plan ?? null,

    precio_minimo_noche: p.precioMinimoNoche ?? null,

    clave_wifi: p.claveWifi ?? null,
    clave_cerradura: p.claveCerradura ?? null,

    mensaje_inicial: p.mensajeInicial ?? null,

    lavanderia_id: p.lavanderiaId ?? null,

    habilitado: p.habilitado !== false,

    correos_cliente_activos: p.correosClienteActivos !== false,
  };
}

/** Extrae filas desde distintas formas típicas de respuesta */
function extractRows<T>(json: any): T[] {
  // casos: { data: [...] }
  if (Array.isArray(json?.data)) return json.data as T[];

  // casos: { rows: [...] }
  if (Array.isArray(json?.rows)) return json.rows as T[];

  // casos: { data: { rows: [...] } }
  if (Array.isArray(json?.data?.rows)) return json.data.rows as T[];

  // casos: respuesta directa array
  if (Array.isArray(json)) return json as T[];

  return [];
}

/** Extrae count desde respuesta (o fallback a rows.length) */
function extractCount(json: any, fallbackLen: number): number {
  const c =
    json?.count ??
    json?.data?.count ??
    json?.data?.total ??
    json?.total ??
    json?.data?.pagination?.total;

  const num = Number(c);
  return Number.isFinite(num) ? num : fallbackLen;
}

export function usePropiedadesLogic() {
  // Filas por página: preferencia, no filtro. La PÁGINA actual no se recuerda
  // (volver y aterrizar en la página 7 desorienta), así que `page` sigue
  // arrancando en 1.
  const [limit, setLimit] = useFiltroPersistido<number>(
    "propiedades.filas",
    10,
    { validar: (v) => [10, 20, 50, 100].includes(v as number) },
  );

  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);

  const [items, setItems] = useState<Propiedad[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [catalogsLoading, setCatalogsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // filtros
  // Contraparte (cliente/propietario): búsqueda en vivo. `ownerSearch` es lo
  // que se tipea; se debouncea a `qOwner`, que es el término que viaja al
  // backend y dispara el fetch. `ownerType` acota a cliente / propietario /
  // ambos (el backend matchea nombre/correo de ambos lados — ver
  // property.controller.js getAllProperties).
  // Se recuerda SOLO `ownerSearch` (lo que se tipea). `qOwner` es su versión
  // debounceada: arranca del mismo texto restaurado para que la primera carga
  // ya salga filtrada — persistir las dos por separado dispararía un fetch de
  // más, y persistir solo `qOwner` dejaría el input vacío con la tabla
  // filtrada, que es el caso más confuso.
  const [ownerSearch, setOwnerSearch] = useFiltroPersistido<string>(
    "propiedades.contraparte",
    "",
    { validar: (v) => typeof v === "string" },
  );
  const [qOwner, setQOwner] = useState<string>(() => ownerSearch.trim());
  const [ownerType, setOwnerType] = useFiltroPersistido<
    "both" | "cliente" | "propietario"
  >("propiedades.tipoContraparte", "both", {
    validar: (v) => v === "both" || v === "cliente" || v === "propietario",
  });
  const ownerDebounceRef = useRef<number | null>(null);
  // Ojo: guarda NOMBRES de propiedad, no ids (así viaja al backend). Si una
  // propiedad se renombra, el nombre guardado deja de matchear y la tabla sale
  // vacía; el Select sigue mostrando el tag, que es la pista para sacarlo.
  const [qProp, setQProp] = useFiltroPersistido<string[]>(
    "propiedades.propiedad",
    [],
    { validar: (v) => Array.isArray(v) && v.every((x) => typeof x === "string") },
  );

  const esListaDeIds = (v: unknown) =>
    Array.isArray(v) && v.every((x) => typeof x === "number" && Number.isFinite(x));

  const [paisIds, setPaisIds] = useFiltroPersistido<number[]>(
    "propiedades.pais",
    [],
    { validar: esListaDeIds },
  );
  const [regionIds, setRegionIds] = useFiltroPersistido<number[]>(
    "propiedades.region",
    [],
    { validar: esListaDeIds },
  );
  const [comunaIds, setComunaIds] = useFiltroPersistido<number[]>(
    "propiedades.comuna",
    [],
    { validar: esListaDeIds },
  );
  const [sectorIds, setSectorIds] = useFiltroPersistido<number[]>(
    "propiedades.sector",
    [],
    { validar: esListaDeIds },
  );

  // catálogos geo (sin normalizar a "nombre": se usa el campo real)
  const [paisOptions, setPaisOptions] = useState<CountryRow[]>([]);
  const [regionOptions, setRegionOptions] = useState<RegionRow[]>([]);
  const [comunaOptions, setComunaOptions] = useState<ComunaRow[]>([]);
  const [sectorOptions, setSectorOptions] = useState<SectorRow[]>([]);
  const [lavanderiaOptions, setLavanderiaOptions] = useState<LavanderiaRow[]>([]);
  const [allPropertyNames, setAllPropertyNames] = useState<
    { id: number; nombrepropiedad: string }[]
  >([]);

  // Columnas visibles (importantes por defecto)
  const defaultVisible = useMemo(
    () => [
      "nombrePropiedad",
      "nombrePublicacion",
      "habilitado",
      "plan",
      "comisionPropiedad",
      "precioLimpiezaCobrado",
      "precioLimpiezaAseador",
      "region",
      "comuna",
      "sector",
      "idPropietario",
      "idCliente",
      "acciones",
    ],
    [],
  );
  // Columnas visibles: preferencia de vista. Se guarda la lista elegida, así
  // que una columna NUEVA del código no aparece sola para quien ya tenga una
  // lista guardada — el botón "Reset (importantes)" del selector la trae.
  const [visibleCols, setVisibleCols] = useFiltroPersistido<string[]>(
    "propiedades.columnas",
    defaultVisible,
    {
      validar: (v) =>
        Array.isArray(v) && v.length > 0 && v.every((x) => typeof x === "string"),
    },
  );
  const [users, setUsers] = useState<any[]>([]);
  const fetchPaisOptions = useCallback(async () => {
    try {
      const json = await propiedadesApi.getCountries();
      const rows = extractRows<CountryRow>(json);
      setPaisOptions(rows);
      return rows;
    } catch (e) {
      console.error("Error al obtener paises", e);
      setPaisOptions([]);
      return [];
    }
  }, []);

  const fetchRegionOptions = useCallback(async () => {
    try {
      const json = await propiedadesApi.getRegions();
      const rows = extractRows<RegionRow>(json);
      setRegionOptions(rows);
      return rows;
    } catch (e) {
      console.error("Error al obtener regiones", e);
      setRegionOptions([]);
      return [];
    }
  }, []);

  const fetchComunaOptions = useCallback(async () => {
    try {
      const json = await propiedadesApi.getComunas();
      const rows = extractRows<ComunaRow>(json);
      setComunaOptions(rows);
      return rows;
    } catch (e) {
      console.error("Error al obtener comunas", e);
      setComunaOptions([]);
      return [];
    }
  }, []);

  const fetchSectorOptions = useCallback(async () => {
    try {
      const json = await propiedadesApi.getSectores();
      const rows = extractRows<SectorRow>(json);
      setSectorOptions(rows);
      return rows;
    } catch (e) {
      console.error("Error al obtener sectores", e);
      setSectorOptions([]);
      return [];
    }
  }, []);

  const fetchLavanderiaOptions = useCallback(async () => {
    try {
      const json = await propiedadesApi.getLavanderias();
      const rows = extractRows<LavanderiaRow>(json);
      setLavanderiaOptions(rows);
      return rows;
    } catch (e) {
      console.error("Error al obtener lavanderías", e);
      setLavanderiaOptions([]);
      return [];
    }
  }, []);

  const fetchAllPropertyNames = useCallback(async () => {
    try {
      const json = await propiedadesApi.getNombres();
      const rows = extractRows<{ id: number; nombrepropiedad: string }>(json);
      setAllPropertyNames(rows);
      return rows;
    } catch (e) {
      console.error("Error al obtener nombres de propiedades", e);
      setAllPropertyNames([]);
      return [];
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const json = await apiFetch<any>("/users/clients-and-owners");
      const rows = extractRows<any>(json);
      setUsers(rows);
    } catch (e) {
      console.error("Error al obtener usuarios", e);
      return [];
    }
  }, []);
  const fetchPropiedades = useCallback(
    async (opts?: {
      page?: number;
      qOwner?: string;
      ownerType?: "both" | "cliente" | "propietario";
      qProp?: string[];
      paisIds?: number[];
      regionIds?: number[];
      comunaIds?: number[];
      sectorIds?: number[];
    }) => {
      const nextPage = opts?.page ?? page;

      const nextQOwner = opts?.qOwner ?? qOwner;
      const nextOwnerType = opts?.ownerType ?? ownerType;
      const nextQProp = opts?.qProp ?? qProp;

      const nextPais = opts?.paisIds ?? paisIds;
      const nextRegion = opts?.regionIds ?? regionIds;
      const nextComuna = opts?.comunaIds ?? comunaIds;
      const nextSector = opts?.sectorIds ?? sectorIds;

      setLoading(true);
      setError(null);

      try {
        const trimmedQOwner = nextQOwner.trim();
        const joinedQProp = nextQProp.join(",");

        const paramsObj: Record<string, string> = {
          page: String(nextPage),
          limit: String(limit),
          ...(trimmedQOwner ? { qOwner: trimmedQOwner } : {}),
          // ownerType solo viaja si hay término y no es "ambos" (default).
          ...(trimmedQOwner && nextOwnerType !== "both"
            ? { ownerType: nextOwnerType }
            : {}),
          ...(joinedQProp ? { qProp: joinedQProp } : {}),
          ...(nextPais.length ? { paisIds: nextPais.join(",") } : {}),
          ...(nextRegion.length ? { regionIds: nextRegion.join(",") } : {}),
          ...(nextComuna.length ? { comunaIds: nextComuna.join(",") } : {}),
          ...(nextSector.length ? { sectorIds: nextSector.join(",") } : {}),
        };

        const json = await propiedadesApi.getAll(paramsObj);
        if (!json) throw new Error("Fetch error");

        const rows = extractRows<ApiPropiedad>(json);
        const count = extractCount(json, rows.length);

        const mapped = rows.map(apiToUi);

        setTotal(count);
        setItems(mapped);
      } catch (e) {
        setError("Error al cargar propiedades.");
      } finally {
        setLoading(false);
      }
    },
    [
      page,
      qOwner,
      ownerType,
      qProp,
      paisIds,
      regionIds,
      comunaIds,
      sectorIds,
      limit,
    ],
  );

  const createPropiedad = useCallback(
    async (payload: PropiedadUpsert) => {
      setLoading(true);
      setError(null);

      try {
        const body = uiToApi(payload);

        const res = await propiedadesApi.create(body);
        if (!res) throw new Error("Create failed");

        setPage(1);
        await fetchPropiedades({ page: 1 });
        // Refrescar la lista de nombres para que el chequeo de duplicados/
        // parecidos del modal vea la propiedad recién creada sin recargar.
        void fetchAllPropertyNames();
        return true;
      } catch (e) {
        setError("Error al crear propiedad.");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [fetchPropiedades, fetchAllPropertyNames],
  );

  const updatePropiedad = useCallback(
    async (id: number, payload: Partial<PropiedadUpsert>) => {
      setLoading(true);
      setError(null);

      try {
        const body = uiToApi(payload);

        const res = await propiedadesApi.update(id, body);
        if (!res) throw new Error("Update failed");

        await fetchPropiedades();
        // Mantener fresca la lista de nombres (un rename cambia los duplicados/
        // parecidos que el modal debe detectar la próxima vez).
        void fetchAllPropertyNames();
        return true;
      } catch (e) {
        setError("Error al actualizar propiedad.");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [fetchPropiedades, fetchAllPropertyNames],
  );

  /**
   * Elimina una propiedad.
   *
   * Round 2026-08-06 — la confirmación es OBLIGATORIA: el backend responde 409
   * mientras quede stock o historial de inventario sin resolver. Los ids salen
   * del previo (`GET /propiedades/:id/impacto-borrado`) que muestra
   * `EliminarPropiedadModal`; llamar sin ellos falla a propósito, para que
   * ningún camino nuevo pueda borrar inventario sin que un humano lo mire.
   */
  const deletePropiedad = useCallback(
    async (
      id: number,
      confirmacion?: {
        eliminarProductos: number[];
        conservarTransacciones: number[];
      },
    ) => {
      setLoading(true);
      setError(null);

      try {
        const res = await propiedadesApi.delete(id, confirmacion);
        if (!res) throw new Error("Delete failed");

        // Mostrar resumen real de lo borrado en cascada — el admin necesita
        // saber el alcance (¿borré 3 reservas o 300?) para detectar errores
        // de selección. El backend devuelve `eliminados` por categoría;
        // construimos un toast que solo lista las que tuvieron filas > 0.
        const labels: Record<string, string> = {
          reservas: "reservas",
          reservasPorIdLargo: "reservas (id largo)",
          aseos: "aseos",
          lavados: "lavados",
          lavanderia: "lav. externa",
          lavanderiaAf: "lav. AF",
          lavanderiaAfGastos: "gastos lav. AF",
          gastos: "gastos",
          reembolsos: "reembolsos",
          bloqueos: "bloqueos",
          contratos: "documentos",
          bodeguitas: "bodeguitas",
          inventario: "ítems inventario",
          inventarioTransacciones: "movs. inventario",
          estadisticas: "meses estadísticas",
          tareasActualizadas: "tareas actualizadas",
        };
        const partes = Object.entries(res.eliminados ?? {})
          .filter(([, n]) => Number(n) > 0)
          .map(([k, n]) => `${n} ${labels[k] ?? k}`);
        const msg = partes.length
          ? `${res.message} — ${partes.join(", ")}.`
          : res.message;
        getMsg()?.success(msg);

        const isLastOnPage = items.length === 1 && page > 1;
        const nextPage = isLastOnPage ? page - 1 : page;

        setPage(nextPage);
        await fetchPropiedades({ page: nextPage });
        return true;
      } catch (e) {
        setError("Error al eliminar propiedad.");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [fetchPropiedades, items.length, page],
  );

  const toggleHabilitado = async (id: number, habilitado: boolean) => {
    try {
      await propiedadesApi.toggleHabilitado(id, habilitado);
      setItems((prev) =>
        prev.map((p) => (p.id === id ? { ...p, habilitado } : p))
      );
    } catch {
      getMsg()?.error("Error al cambiar habilitado");
    }
  };

  const createUsuario = useCallback(
    async (
      payload: UsuarioCreate,
      opciones: OpcionesLlamada = {},
    ): Promise<{ ok: true; id: number } | { ok: false; message: string }> => {
      try {
        // Post-refactor: rol escalar. Aceptamos array legacy como defensivo.
        const tipo =
          (Array.isArray(payload.rol)
            ? (payload.rol as unknown as string[])[0]
            : payload.rol) ?? "cliente";
        const upsertBody = {
          tipo,
          user: {
            nombrePropietario: payload.nombre_propietario ?? null,
            emailPropietario: payload.email_propietario ?? null,
            telefonoPropietario: payload.telefono_propietario ?? null,
            nombreUsuario: payload.nombre_usuario ?? null,
            emailUsuario: payload.email_usuario ?? null,
            telefonoUsuario: payload.telefono_usuario ?? null,
          },
        };
        const json = await apiFetch<any>("/users/upsert", {
          method: "POST",
          body: JSON.stringify(upsertBody),
          // El formulario se entera de POR QUÉ falló (p. ej. un teléfono repetido) para marcar el campo.
          alFallar: opciones.alFallar,
        });

        if (!json) {
          // apiFetch retornó null — puede que haya mostrado toast o no.
          // Forzamos un fallback visible.
          return { ok: false, message: "No se pudo crear el usuario. Verifica que el email no esté ya registrado." };
        }

        const newId = Number(json?.data?.id ?? json?.id);
        if (!Number.isFinite(newId)) {
          await fetchUsers();
          return {
            ok: false,
            message: "Usuario creado, pero no se pudo obtener el ID.",
          };
        }

        await fetchUsers();
        return { ok: true, id: newId };
      } catch (e) {
        return { ok: false, message: "Error al crear usuario." };
      }
    },
    [fetchUsers],
  );

  const applyFilters = useCallback(
    async (next?: {
      qOwner?: string;
      qProp?: string[];
      paisIds?: number[];
      regionIds?: number[];
      comunaIds?: number[];
      sectorIds?: number[];
    }) => {
      const nQOwner = next?.qOwner ?? qOwner;
      const nQProp = next?.qProp ?? qProp;

      const nPais = next?.paisIds ?? paisIds;
      const nRegion = next?.regionIds ?? regionIds;
      const nComuna = next?.comunaIds ?? comunaIds;
      const nSector = next?.sectorIds ?? sectorIds;

      setQOwner(nQOwner);
      setQProp(nQProp);

      setPaisIds(nPais);
      setRegionIds(nRegion);
      setComunaIds(nComuna);
      setSectorIds(nSector);

      setPage(1);
      await fetchPropiedades({
        page: 1,
        qOwner: nQOwner,
        qProp: nQProp,
        paisIds: nPais,
        regionIds: nRegion,
        comunaIds: nComuna,
        sectorIds: nSector,
      });
    },
    [
      fetchPropiedades,
      qOwner,
      qProp,
      paisIds,
      regionIds,
      comunaIds,
      sectorIds,
      setQProp,
      setPaisIds,
      setRegionIds,
      setComunaIds,
      setSectorIds,
    ],
  );

  const clearGeoFilters = useCallback(() => {
    setPaisIds([]);
    setRegionIds([]);
    setComunaIds([]);
    setSectorIds([]);
    setPage(1);
    // Los setters de useFiltroPersistido son estables; van declarados solo
    // porque ESLint no puede probarlo.
  }, [setPaisIds, setRegionIds, setComunaIds, setSectorIds]);

  // Debounce de la búsqueda de contraparte: 1 fetch tras dejar de teclear,
  // no uno por cada tecla. Al cambiar el término volvemos a la página 1.
  useEffect(() => {
    if (ownerDebounceRef.current) window.clearTimeout(ownerDebounceRef.current);
    ownerDebounceRef.current = window.setTimeout(() => {
      setQOwner(ownerSearch.trim());
      setPage(1);
    }, 300);
    return () => {
      if (ownerDebounceRef.current)
        window.clearTimeout(ownerDebounceRef.current);
    };
  }, [ownerSearch]);

  useEffect(() => {
    fetchPropiedades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    limit,
    qOwner,
    ownerType,
    qProp,
    paisIds,
    regionIds,
    comunaIds,
    sectorIds,
  ]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setCatalogsLoading(true);
      await Promise.allSettled([
        fetchPaisOptions(),
        fetchRegionOptions(),
        fetchComunaOptions(),
        fetchSectorOptions(),
        fetchLavanderiaOptions(),
        fetchUsers(),
        fetchAllPropertyNames(),
      ]);
      if (mounted) setCatalogsLoading(false);
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // Options de región/comuna para los filtros avanzados = catálogo COMPLETO.
  // Antes se recortaban a las regiones/comunas presentes en `items`, pero
  // `items` es la PÁGINA ACTUAL de la tabla (paginación server-side): una
  // región cuyas propiedades no caían en la página visible desaparecía del
  // dropdown, y al aplicar cualquier filtro las opciones se encogían en
  // cascada (auditoría 2026-07-13). Se mantienen los nombres exportados para
  // no tocar los consumidores.
  const filteredRegionOptions = regionOptions;
  const filteredComunaOptions = comunaOptions;

  return {
    limit,
    setLimit,

    items,
    loading,
    catalogsLoading,
    error,

    page,
    total,
    setPage,

    // filtros
    ownerSearch,
    setOwnerSearch,
    ownerType,
    setOwnerType,
    qProp,
    setQProp,

    paisIds,
    setPaisIds,
    regionIds,
    setRegionIds,
    comunaIds,
    setComunaIds,
    sectorIds,
    setSectorIds,

    applyFilters,
    clearGeoFilters,
    fetchUsers,
    // catálogos
    paisOptions,
    regionOptions,
    comunaOptions,
    sectorOptions,
    // setters/fetchers para creación inline desde el modal
    setSectorOptions,
    fetchSectorOptions,
    lavanderiaOptions,
    filteredRegionOptions,
    filteredComunaOptions,
    users,
    allPropertyNames,
    // columnas
    visibleCols,
    setVisibleCols,
    defaultVisible,

    // CRUD
    fetchPropiedades,
    createPropiedad,
    updatePropiedad,
    deletePropiedad,
    toggleHabilitado,
    createUsuario,
    // sidebar
    sidebarOpen,
    toggleSidebar,
  };
}
